import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, articles } from '@/mocks/siteData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const filteredArticles = articles.filter((a) => a.category === slug);

  if (!currentCategory) {
    return (
      <div className="pt-20">
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
            <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-heading font-semibold text-foreground-900 mb-2">分类未找到</h1>
          <p className="text-sm text-foreground-600 mb-6">你访问的分类不存在或已被移除。</p>
          <Link to="/" className="text-sm text-accent-600 hover:text-accent-700 font-medium whitespace-nowrap cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16">
      <div className="bg-background-100 border-b border-background-200">
        <div className="max-w-6xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory.name },
            ]}
          />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
              <i className={`${currentCategory.icon} text-lg`}></i>
            </div>
            <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950">
              {currentCategory.name}
            </h1>
          </div>
          <p className="text-sm text-foreground-600 max-w-2xl">{currentCategory.description}</p>
        </div>

        <div className="flex items-center gap-2 flex-wrap mb-8 pb-6 border-b border-background-200">
          <span className="text-xs text-foreground-500 mr-2 whitespace-nowrap">切换分类：</span>
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                cat.slug === slug
                  ? 'bg-accent-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              {cat.name}
            </Link>
          ))}
        </div>

        {filteredArticles.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
              <i className="ri-file-list-2-line text-xl text-foreground-400"></i>
            </div>
            <p className="text-sm text-foreground-600">该分类下暂无文章，敬请期待。</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {filteredArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-accent-300 transition-all duration-200 cursor-pointer flex flex-col"
              >
                <div className="relative w-full h-40 overflow-hidden">
                  <img
                    src={article.imageUrl}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <h3 className="font-heading font-semibold text-foreground-900 text-sm md:text-base mb-2 line-clamp-2 group-hover:text-accent-700 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2 mb-3 flex-1">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {article.readTime} 分钟
                    </span>
                    <span>{article.publishedAt}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}